import { SortBucket } from "./components/SortBucket";
import { useWarehouseContext } from "./WarehouseProvider";
import { SORTED_PICTURES } from "./WarehouseProvider";
import React, { useRef, useEffect } from "react";
import { StepHeader } from "./components/StepHeader";
import { FullscreenModal } from "./components/FullscreenModal";
import { useSteps } from "./useSteps";
import {
  WarehouseButtonsFooter,
  WarehouseButton,
  WarehouseButtonGroup,
} from "./components/WarehouseButtonsFooter";

// Import placeholder images
import bucketAllPlaceholder from "./images/placeholders/bucket-all.png";
import bucket2Placeholder from "./images/placeholders/bucket-2.png";
import bucket3aPlaceholder from "./images/placeholders/bucket-3a.png";
import bucket3bPlaceholder from "./images/placeholders/bucket-3b.png";
import bucket3cPlaceholder from "./images/placeholders/bucket-3c.png";
import bucket3dPlaceholder from "./images/placeholders/bucket-3d.png";

const TITLE = "Sort Contents";

export const SortContents = () => {
  const {
    sortedPictures,
    setSortedPicture,
    selectedBucket,
    setSelectedBucket,
  } = useWarehouseContext();
  const { next, previous, reject } = useSteps();
  const firstBucketRef = useRef(null);
  const bucket2Ref = useRef(null);
  const bucket3aRef = useRef(null);
  const bucket3bRef = useRef(null);
  const bucket3cRef = useRef(null);
  const bucket3dRef = useRef(null);

  useEffect(() => {
    // Focus the first bucket when component mounts, but only if it doesn't have a picture
    if (firstBucketRef.current && !sortedPictures[SORTED_PICTURES.all]) {
      firstBucketRef.current.focus();
    }
  }, [sortedPictures]);

  useEffect(() => {
    // Remove focus from first bucket when it gets a picture
    if (sortedPictures[SORTED_PICTURES.all] && firstBucketRef.current) {
      firstBucketRef.current.blur();
    }
  }, [sortedPictures]);

  useEffect(() => {
    // Add Enter key handler when first bucket has a picture
    const handleKeyPress = (event) => {
      if (event.key === "Enter" && sortedPictures[SORTED_PICTURES.all]) {
        next();
      }
    };

    window.addEventListener("keypress", handleKeyPress);
    return () => {
      window.removeEventListener("keypress", handleKeyPress);
    };
  }, [sortedPictures, next]);

  useEffect(() => {
    // Handle selected bucket from Summary screen
    if (selectedBucket) {
      const bucketRefs = {
        all: firstBucketRef,
        bucket2: bucket2Ref,
        bucket3a: bucket3aRef,
        bucket3b: bucket3bRef,
        bucket3c: bucket3cRef,
        bucket3d: bucket3dRef,
      };

      const targetRef = bucketRefs[selectedBucket];
      if (targetRef?.current) {
        // Small delay to ensure component is fully rendered
        setTimeout(() => {
          targetRef.current.click();
          // Clear the selected bucket after clicking
          setSelectedBucket(null);
        }, 100);
      }
    }
  }, [selectedBucket, setSelectedBucket]);

  return (
    <FullscreenModal>
      <div className="flex flex-col h-full">
        <StepHeader />

        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="flex flex-col items-center gap-8">
            <div className="flex flex-col items-center gap-4">
              <h1 className="text-[29px] font-bold text-white leading-[37px] tracking-[-0.01em]">
                {TITLE}
              </h1>
              <div className="text-[18px] font-normal text-[#C6CCE8] leading-[23px] text-center max-w-[780px]">
                <span className="whitespace-nowrap">
                  Remove any non-conform items, place them in the relevant
                  bucket, and take an optional photo.
                </span>{" "}
                Finally, take a photo of the conform items left in the
                container.
              </div>
            </div>

            <div className="grid grid-cols-3 grid-rows-2 gap-x-6 gap-y-6 items-start">
              <SortBucket
                ref={firstBucketRef}
                onPhotoCaptured={(picture) => {
                  setSortedPicture(SORTED_PICTURES.all, picture);
                }}
                picture={sortedPictures[SORTED_PICTURES.all]}
                heading="Leave in tray"
                description="Conform LEGO®"
                label="Required"
                placeholder={bucketAllPlaceholder}
                isRequired={true}
              />
              <SortBucket
                ref={bucket2Ref}
                onPhotoCaptured={(picture) => {
                  setSortedPicture(SORTED_PICTURES.bucket2, picture);
                }}
                picture={sortedPictures[SORTED_PICTURES.bucket2]}
                heading="Bucket 2"
                description="Assembled Non-conform"
                placeholder={bucket2Placeholder}
              />
              <SortBucket
                ref={bucket3aRef}
                onPhotoCaptured={(picture) => {
                  setSortedPicture(SORTED_PICTURES.bucket3a, picture);
                }}
                picture={sortedPictures[SORTED_PICTURES.bucket3a]}
                heading="Bucket 3"
                description="Non-conform DUPLO®"
                placeholder={bucket3aPlaceholder}
              />
              <SortBucket
                ref={bucket3bRef}
                onPhotoCaptured={(picture) => {
                  setSortedPicture(SORTED_PICTURES.bucket3b, picture);
                }}
                picture={sortedPictures[SORTED_PICTURES.bucket3b]}
                heading="Bucket 4"
                description="Poor quality / dirty LEGO"
                placeholder={bucket3bPlaceholder}
              />
              <SortBucket
                ref={bucket3cRef}
                onPhotoCaptured={(picture) => {
                  setSortedPicture(SORTED_PICTURES.bucket3c, picture);
                }}
                picture={sortedPictures[SORTED_PICTURES.bucket3c]}
                heading="Bucket 5"
                description="Any non-LEGO branded items"
                placeholder={bucket3cPlaceholder}
              />
              <SortBucket
                ref={bucket3dRef}
                onPhotoCaptured={(picture) => {
                  setSortedPicture(SORTED_PICTURES.bucket3d, picture);
                }}
                picture={sortedPictures[SORTED_PICTURES.bucket3d]}
                heading="Bucket 6"
                description="Hazardous"
                placeholder={bucket3dPlaceholder}
              />
            </div>
          </div>
        </div>

        <WarehouseButtonsFooter>
          <WarehouseButtonGroup>
            <WarehouseButton variant="default" onClick={() => previous()}>
              Back
            </WarehouseButton>
            <WarehouseButton variant="reject" onClick={() => reject()}>
              Reject
            </WarehouseButton>
            <WarehouseButton variant="primary" onClick={() => next()}>
              Continue
            </WarehouseButton>
          </WarehouseButtonGroup>
        </WarehouseButtonsFooter>
      </div>
    </FullscreenModal>
  );
};

SortContents.title = TITLE;
